// import './wdyr'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
// import * as Sentry from '@sentry/browser'
import { setAppLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, targetEnv } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClientProvider } from '@tanstack/react-query'
import whyDidYouRender from '@welldone-software/why-did-you-render'
import { BrightspotProvider } from '@fs/zion-cms'
import { useFeatureFlag } from '@fs/zion-flags'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import useScreenSize from './hooks/useScreenSize'
import { AppProvider, useAppProvider } from './providers/AppProvider'
import queryClient from './queryClient'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const GiveRoot = () => {
  const screenSize = useScreenSize()
  const { shareablePage, skipLinkId } = useAppProvider()
  const newRecordHints = useFeatureFlag('give_new_record_hints').isOn

  const header = {
    hidden: shareablePage,
  }

  const footer = {
    hidden: shareablePage || newRecordHints || screenSize.height < 900,
    useFatFooter: true,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename={base}>
        <Root analytics header={header} footer={footer} skipLinkId={skipLinkId}>
          <AppLayout fullWidth noScroll={newRecordHints}>
            <App />
          </AppLayout>
        </Root>
        <ReactQueryDevtools />
      </Router>
    </QueryClientProvider>
  )
}

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(['cs', 'da', 'de', 'en', 'es', 'fr', 'hu', 'it', 'ja', 'ko', 'no', 'pt', 'ro', 'ru', 'sm', 'uk', 'zh'])

  return (
    <BrightspotProvider sites={['global-campaigns']}>
      <AppProvider>
        <GiveRoot />
      </AppProvider>
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  )
}

if (process.env.NODE_ENV !== 'production') {
  whyDidYouRender(React, { include: [] })
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()

if (targetEnv === 'local') {
  ;(async () => {
    const { default: axe } = await import('@axe-core/react')
    axe(React, ReactDOM, 1000)
  })()
}
